import { ArrowBigUp, FastForward, Hand, Pause, Smartphone, Utensils } from 'lucide-preact'
import { text } from '#contract'
import type { ViewState } from '#contract'
import { useClient } from '../app/client-context.ts'
import { useView } from '../app/use-view.ts'
import { useT } from '../i18n/index.ts'
import { Joystick } from './joystick.tsx'
import { LookArea } from './look-area.tsx'
import { TouchButton } from './touch-button.tsx'
import buttonStyles from './touch-button.module.css'
import styles from './touch-controls.module.css'

/** Playing on a touch screen that is still the last-used device (the default on touch). */
export function isTouchPlaying(view: ViewState): boolean {
  return view.phase === 'playing' && view.input.touchAvailable && view.input.device === 'touch'
}

/**
 * The on-screen controls show while touch-playing, except under the inventory screen or the
 * OS, and while the camera is taken (sitting down).
 */
const controlsShown = (view: ViewState): boolean =>
  isTouchPlaying(view) &&
  (view.inventory?.screen ?? null) === null &&
  view.os.open === null &&
  !view.hud.takeover

/** With the phone up, only its button stays, to put it away. */
const phoneUp = (view: ViewState): boolean => isTouchPlaying(view) && view.os.open === 'phone'

/**
 * The on-screen controls: a floating joystick on the left, look-drag on the right, and
 * buttons on top. Interact, Skip and Use (hold to eat or drink) show only while there's
 * something to do. Presentation only:
 * everything goes to `client.touch`.
 */
export function TouchControls() {
  const t = useT()
  const client = useClient()
  const shown = useView(controlsShown)
  const phone = useView(phoneUp)
  const prompt = useView((view) => view.hud.prompt)
  const skip = useView((view) => view.hud.skip)
  const use = useView((view) => view.inventory?.use ?? null)
  if (!client) return null
  const phoneButton = (
    <button
      type="button"
      tabIndex={-1}
      class={[buttonStyles.button, styles.phone].join(' ')}
      aria-label={t(text('touch.phone'))}
      onClick={() => {
        client.os.togglePhone()
      }}
    >
      <Smartphone class={buttonStyles.icon} aria-hidden="true" />
    </button>
  )
  if (phone) return phoneButton
  if (!shown) return null

  return (
    <div class={styles.controls}>
      <Joystick touch={client.touch} />
      <LookArea touch={client.touch} />
      <TouchButton
        class={styles.pause}
        action="pause"
        icon={Pause}
        label={t(text('touch.pause'))}
        touch={client.touch}
      />
      {phoneButton}
      {prompt && (
        <TouchButton
          class={styles.interact}
          action="interact"
          icon={Hand}
          label={t(prompt.text)}
          touch={client.touch}
        />
      )}
      {skip && (
        <TouchButton
          class={styles.skip}
          action="skip"
          icon={FastForward}
          label={t(skip)}
          touch={client.touch}
        />
      )}
      {use && (
        <TouchButton
          class={styles.use}
          action="useItem"
          icon={Utensils}
          label={t(use.text)}
          touch={client.touch}
        />
      )}
      <TouchButton
        class={styles.jump}
        action="jump"
        icon={ArrowBigUp}
        label={t(text('touch.jump'))}
        touch={client.touch}
      />
    </div>
  )
}
